/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { useUser } from '../../context/UserContext';
import usePermission from '../../hooks/usePermission';
import {
  UserCircle2, Settings, User, Building, CreditCard, Shield, Lock, PersonStanding,
  UserCog, Eye, EyeOff, MessageCircle, CheckCircle, Mail, Phone, MapPin, Calendar,
  Hash, Briefcase, BadgeCheck, AlertCircle, Clock, KeyRound, Sparkles, Layers,
} from 'lucide-react';

import { setPassword } from '../../services/login';
import { toast } from 'react-toastify';
import RolesDashboard from '../RoleManagement/RolesDashboard';
import ManageRolesPage from '../RoleManagement/ManageRolesPage';
import UsageGraph from './UsageGraph'; // Import the new component
import CustomInput from '../../components/CustomInput';
import axiosInstance from '../../services/api/axios';
import { OutlineButton, PrimaryButton } from '../../components/Buttons';
import { useNavigate } from 'react-router-dom';
import Onboarding from '../../components/Onboarding';
import { encryptForTransit, decryptFromTransit } from '../../utils/common';
import useSubscriptionFeatures from '../../hooks/useSubscriptionFeatures';
import { SUB_FEATURES, FEATURE_GATE_TOOLTIP } from '../../constants/subscriptionFeatures';


function normalizeProfileRoleName(roleName) {
  if (!roleName || typeof roleName !== 'string') return '';
  return roleName.trim().toUpperCase().replace(/-/g, '_');
}

function Profile() {
  const { userInfo, setUserInfo } = useUser();
  const { isDeveloperAdmin } = usePermission();
  const { hasFeature } = useSubscriptionFeatures();
  const navigate = useNavigate();

  const [activeSection, setActiveSection] = useState('profile');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const persona = userInfo?.persona;
  const isInteriorDesignerProfile = persona === 'INTERIOR_DESIGNER';
  const isBvUserProfile = persona === 'BV_ASSOCIATE';
  const isLimitedSettingsRole = isInteriorDesignerProfile || isBvUserProfile;

  const menuItems = useMemo(() => {
    if (isLimitedSettingsRole) {
      if (isInteriorDesignerProfile) {
        return [
          { id: 'password', label: 'Change Password', icon: Lock },
          { id: 'security', label: 'Security & Privacy', icon: Shield },
        ];
      }
      return [
        { id: 'profile', label: 'Profile Overview', icon: User },
        { id: 'password', label: 'Change Password', icon: Lock },
        { id: 'security', label: 'Security & Privacy', icon: Shield },
      ];
    }
    return [
      // Per RBAC spec: Onboarding is visible only to Developer Admin.
      ...(isDeveloperAdmin() ? [
        { id: 'onboarding', label: 'Onboarding Setup', icon: CheckCircle },
      ] : []),
      { id: 'profile', label: 'Profile Overview', icon: User },
      ...(userInfo?.persona !== 'CHANNEL_PARTNER' && userInfo?.persona !== 'STAFF' ? [
        { id: 'customer', label: 'Customer Details', icon: Building },
        { id: 'subscription', label: 'Subscription', icon: CreditCard },
        { id: 'roles', label: 'Users', icon: UserCog },
        { id: 'rbac', label: 'Role Based Access', icon: KeyRound },
        // "Interior Designer Requests" moved to the dedicated Interior Designers
        // admin section (/zumale-admin/interior-designers).
      ] : []),
      { id: 'security', label: 'Security & Privacy', icon: Shield },
      { id: 'password', label: 'Change Password', icon: Lock },
    ];
  }, [
    isLimitedSettingsRole,
    isInteriorDesignerProfile,
    isDeveloperAdmin,
    userInfo?.persona,
    userInfo?.loginIdentifierDetails?.customer?.customerType,
  ]);
  const [isSavingWhatsapp, setIsSavingWhatsapp] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [whatsappConfig, setWhatsappConfig] = useState({
    metaId: userInfo?.loginIdentifierDetails?.customer?.metaId || "",
    whatsappSecretAccessToken: userInfo?.loginIdentifierDetails?.customer?.whatsappSecretAccessToken || "",
    whatsappBusinessAccountId: userInfo?.loginIdentifierDetails?.customer?.whatsappBusinessAccountId || "",
    whatsappAppId: userInfo?.loginIdentifierDetails?.customer?.whatsappAppId || "",
  });
  const [whatsappVisible, setWhatsappVisible] = useState({
    metaId: false,
    whatsappSecretAccessToken: false,
    whatsappBusinessAccountId: false,
    whatsappAppId: false,
  });



  // Add useEffect to handle hash changes
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      let defaultSection = 'profile';

      if (isLimitedSettingsRole) {
        defaultSection = isInteriorDesignerProfile ? 'password' : 'profile';
      } else if (isDeveloperAdmin()) {
        defaultSection = 'onboarding';
      }

      const section = hash || defaultSection;
      if (menuItems.some(item => item.id === section)) {
        setActiveSection(section);
      } else {
        setActiveSection(defaultSection);
      }
    };

    // Set initial section based on hash
    handleHashChange();

    // Add event listener for hash changes
    window.addEventListener('hashchange', handleHashChange);

    // Cleanup
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [userInfo?.persona, isLimitedSettingsRole, isInteriorDesignerProfile, isDeveloperAdmin, menuItems]);

  // Modify the section change handler
  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    window.location.hash = sectionId;
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validation checks
      if (newPassword !== confirmPassword) {
        toast.error('New passwords do not match');
        return;
      }
      await setPassword(userInfo.username, newPassword);

      // Success handling
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

    } catch (error) {
      toast.error('Failed to change password. Please try again.');
      console.error('Password change error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  // Label + value rows. Brand palette is flamingo + slate/grey only — any
  // legacy accent names alias to one of those three so existing call-sites
  // continue to work without picking up a third tertiary colour.
  const InfoRow = ({ icon: Icon, label, value, span = false, accent = 'gray' }) => {
    // Two accent tones only: brand (flamingo) and neutral (slate/grey).
    const accentMap = {
      gray: 'bg-gray-100 text-gray-600',
      slate: 'bg-slate-100 text-slate-700',
      flamingo: 'bg-flamingo-50 text-flamingo-600',
    };
    return (
      <div className={`flex items-start gap-3 ${span ? 'md:col-span-2' : ''}`}>
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
            accentMap[accent] || accentMap.gray
          }`}
        >
          <Icon size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
          <p className="text-sm font-semibold text-gray-900 mt-0.5 break-words">
            {value || <span className="text-gray-400 font-normal">Not set</span>}
          </p>
        </div>
      </div>
    );
  };

  const SectionCard = ({ title, subtitle, action, children }) => (
    <section className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 bg-gradient-to-r from-slate-800 to-slate-600 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-white">{title}</h2>
          {subtitle && <p className="text-xs text-slate-300 mt-0.5">{subtitle}</p>}
        </div>
        {action}
      </div>
      <div className="px-6 py-6 space-y-5">{children}</div>
    </section>
  );

  const handleWhatsappConfigChange = (e) => {
    const { name, value } = e.target;
    setWhatsappConfig({
      ...whatsappConfig,
      [name]: value,
    });
  };

  const handleSaveWhatsAppConfig = async () => {
    setIsSavingWhatsapp(true);
    try {
      const payload = {
        metaId: whatsappConfig.metaId,
        whatsappSecretAccessToken: encryptForTransit(whatsappConfig.whatsappSecretAccessToken),
        whatsappBusinessAccountId: whatsappConfig.whatsappBusinessAccountId,
        whatsappAppId: whatsappConfig.whatsappAppId,
      };

      // Correct PUT URL
      await axiosInstance.put(`/customers?customerId=${userInfo.loginIdentifierDetails.customer.customerId}`, payload);
      // Fetch updated login info and update context
      const response = await axiosInstance.get(`/login?loginId=${userInfo.loginId}`);
      const updatedUser = Array.isArray(response) ? response[0] : response;
      setUserInfo(updatedUser);
      setIsEditing(false);
      toast.success('WhatsApp configuration saved successfully!');
    } catch (error) {
      console.error('Error saving WhatsApp configuration:', error);
      toast.error('Failed to save WhatsApp configuration. Please try again.');
    } finally {
      setIsSavingWhatsapp(false);
    }
  };


  const handleWhatsAppSetup = () => {
    navigate('/whatsapp/setup');
  };


  const toggleWhatsappVisibility = (id) => {
    setWhatsappVisible((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Add auto-resize helper
  const autoResize = (el) => {
    if (el) {
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'onboarding':
        if (!isDeveloperAdmin()) return null;
        if (!hasFeature(SUB_FEATURES.ONBOARDING_STEPS)) {
          return (
            <div className="flex flex-col items-center justify-center py-16 text-center bg-white rounded-2xl shadow-sm border border-gray-200">
              <div className="rounded-full bg-flamingo-50 p-3 mb-3">
                <CheckCircle size={22} className="text-flamingo-500" />
              </div>
              <p className="text-sm font-medium text-slate-700">{FEATURE_GATE_TOOLTIP}</p>
              <p className="text-xs text-slate-500 mt-1">
                Onboarding setup is available on BuildVizer Unified.
              </p>
            </div>
          );
        }
        return <Onboarding />;
      case 'profile':
        return (
          <section className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Hero header with avatar */}
            <div className="relative px-8 py-8 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700">
              <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_30%_20%,white,transparent_50%)]" />
              <div className="relative flex flex-col sm:flex-row items-center sm:items-start sm:space-x-6 space-y-4 sm:space-y-0">
                <div className="relative shrink-0">
                  {userInfo?.profilePicture ? (
                    <img
                      src={userInfo.profilePicture}
                      alt="Profile"
                      className="w-24 h-24 rounded-2xl border-2 border-white/30 shadow-lg"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-2xl border-2 border-white/30 bg-white/10 backdrop-blur flex items-center justify-center shadow-lg">
                      <UserCircle2 size={56} className="text-white" />
                    </div>
                  )}
                  <span
                    className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full ${
                      userInfo?.active ? 'bg-flamingo-400' : 'bg-rose-400'
                    } border-2 border-slate-900 shadow`}
                  />
                </div>
                <div className="text-center sm:text-left text-white flex-1 min-w-0">
                  <h1 className="text-2xl sm:text-3xl font-bold tracking-tight truncate">
                    {userInfo?.name || 'N/A'}
                  </h1>
                  <p className="text-slate-300 text-sm mt-0.5 truncate">@{userInfo?.username || 'N/A'}</p>
                  <div className="mt-3 flex flex-wrap gap-2 justify-center sm:justify-start">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-flamingo-500 text-white shadow-sm">
                      <BadgeCheck size={14} />
                      {(userInfo?.persona || 'NO_ROLE').replace(/_/g, ' ')}
                    </span>
                    <span
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                        userInfo?.active ? 'bg-flamingo-500/20 text-flamingo-200' : 'bg-rose-500/20 text-rose-200'
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          userInfo?.active ? 'bg-flamingo-400' : 'bg-rose-400'
                        }`}
                      />
                      {userInfo?.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-6 sm:p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {userInfo?.persona === 'CHANNEL_PARTNER' ? (
                  <>
                    <InfoRow
                      icon={Mail}
                      label="Agent Email"
                      value={userInfo?.loginIdentifierDetails?.emailId}
                      accent="indigo"
                    />
                    <InfoRow
                      icon={Calendar}
                      label="Created Date"
                      value={new Date().toLocaleDateString()}
                      accent="gray"
                    />
                    <InfoRow
                      icon={MessageCircle}
                      label="Agent WhatsApp"
                      value={userInfo?.loginIdentifierDetails?.mobileNoWa}
                      accent="flamingo"
                    />
                    <InfoRow
                      icon={Phone}
                      label="Agent Phone"
                      value={userInfo?.loginIdentifierDetails?.mobileNoCalling}
                      accent="flamingo"
                    />
                  </>
                ) : (
                  <>
                    <InfoRow
                      icon={Mail}
                      label="Email"
                      value={
                        userInfo?.persona !== 'STAFF' && !isBvUserProfile && !isInteriorDesignerProfile
                          ? userInfo?.loginIdentifierDetails?.customer?.emailId
                          : userInfo?.email || userInfo?.loginIdentifierDetails?.customer?.emailId
                      }
                      accent="indigo"
                    />
                    <InfoRow
                      icon={Calendar}
                      label="Created"
                      value={
                        userInfo?.createdDate
                          ? new Date(userInfo.createdDate).toLocaleDateString(undefined, {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })
                          : null
                      }
                      accent="gray"
                    />
                    <InfoRow
                      icon={MessageCircle}
                      label="WhatsApp"
                      value={
                        userInfo?.persona === 'STAFF' || isBvUserProfile || isInteriorDesignerProfile
                          ? userInfo?.mobileNoWa
                          : userInfo?.loginIdentifierDetails?.customer?.mobileNoWa
                      }
                      accent="flamingo"
                    />
                    {userInfo?.persona !== 'STAFF' && (
                      <InfoRow
                        icon={Phone}
                        label="Phone"
                        value={
                          isBvUserProfile || isInteriorDesignerProfile
                            ? userInfo?.mobileNoCalling || userInfo?.phone
                            : userInfo?.loginIdentifierDetails?.customer?.mobileNoCalling
                        }
                        accent="flamingo"
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          </section>
        );

      case 'customer':
        return userInfo?.persona !== 'CHANNEL_PARTNER' && userInfo?.loginIdentifierDetails?.customer && (
          <SectionCard
            title="Customer Details"
            subtitle="Organisation registered with this account"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <InfoRow
                icon={Building}
                label="Company Name"
                value={userInfo.loginIdentifierDetails.customer.companyName}
                accent="indigo"
              />
              <InfoRow
                icon={User}
                label="Owner Name"
                value={userInfo.loginIdentifierDetails.customer.ownerName}
                accent="flamingo"
              />
              <InfoRow
                icon={Hash}
                label="RERA Number"
                value={userInfo.loginIdentifierDetails.customer.reraNumber}
                accent="slate"
                span
              />
            </div>

            {/* Address card */}
            <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-gray-50 to-white p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-flamingo-100 text-flamingo-700 flex items-center justify-center">
                  <MapPin size={15} />
                </div>
                <h3 className="text-sm font-semibold text-gray-900">Registered Address</h3>
              </div>
              <address className="not-italic text-sm text-gray-700 leading-relaxed pl-10">
                {userInfo.loginIdentifierDetails.customer.address.buildingNo},{' '}
                {userInfo.loginIdentifierDetails.customer.address.streetName1}
                {userInfo.loginIdentifierDetails.customer.address.streetName2 && (
                  <>
                    <br />
                    {userInfo.loginIdentifierDetails.customer.address.streetName2}
                  </>
                )}
                <br />
                {userInfo.loginIdentifierDetails.customer.address.city},{' '}
                {userInfo.loginIdentifierDetails.customer.address.district}
                <br />
                {userInfo.loginIdentifierDetails.customer.address.state},{' '}
                {userInfo.loginIdentifierDetails.customer.address.country}
                {' — '}
                <span className="font-mono font-medium text-gray-900">
                  {userInfo.loginIdentifierDetails.customer.address.pinCode}
                </span>
              </address>
            </div>
          </SectionCard>
        );


      case 'subscription': {
        if (userInfo?.persona === 'CHANNEL_PARTNER' || !userInfo?.loginIdentifierDetails?.subscription) {
          return null;
        }
        const sub = userInfo.loginIdentifierDetails.subscription;
        const typeName = sub.subscriptionType?.typeName || 'PLAN';
        const endDate = sub?.endDate ? new Date(sub.endDate) : null;
        const now = new Date();
        const daysLeft = endDate
          ? Math.max(0, Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)))
          : null;
        const expired = endDate ? endDate < now : false;
        const planStyles = {
          TRIAL: 'bg-gray-100 text-gray-700 border-gray-200',
          STANDARD: 'bg-flamingo-100 text-flamingo-700 border-flamingo-200',
          PREMIUM: 'bg-slate-800 text-white border-slate-800',
        };
        const planStyle = planStyles[typeName] || 'bg-gray-100 text-gray-700 border-gray-200';
        return (
          <SectionCard
            title="Subscription"
            subtitle="Your current plan and billing window"
            action={
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${planStyle}`}
              >
                <Sparkles size={12} />
                {typeName}
              </span>
            }
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <InfoRow
                icon={Layers}
                label="Plan"
                value={typeName}
                accent="indigo"
              />
              <InfoRow
                icon={CreditCard}
                label="Price"
                value={
                  sub.price != null
                    ? `${sub.currency?.symbol || ''}${sub.price}`
                    : null
                }
                accent="flamingo"
              />
              <InfoRow
                icon={Calendar}
                label="Start Date"
                value={sub?.startDate ? new Date(sub.startDate).toLocaleDateString(undefined, {
                  year: 'numeric', month: 'long', day: 'numeric',
                }) : null}
                accent="gray"
              />
              <InfoRow
                icon={Clock}
                label={expired ? 'Ended' : 'Expires'}
                value={
                  endDate ? (
                    <span className="flex items-center gap-2">
                      <span>{endDate.toLocaleDateString(undefined, {
                        year: 'numeric', month: 'long', day: 'numeric',
                      })}</span>
                      {!expired && daysLeft !== null && (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${
                            daysLeft < 14
                              ? 'bg-rose-100 text-rose-700'
                              : daysLeft < 30
                              ? 'bg-slate-200 text-slate-700'
                              : 'bg-flamingo-100 text-flamingo-700'
                          }`}
                        >
                          {daysLeft}d left
                        </span>
                      )}
                      {expired && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-rose-100 text-rose-700">
                          Expired
                        </span>
                      )}
                    </span>
                  ) : null
                }
                accent={expired ? 'gray' : 'flamingo'}
              />
            </div>

            {typeName === 'STANDARD' && (
              <div className="pt-2">
                <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Usage</h3>
                  <UsageGraph customerId={userInfo.loginIdentifierDetails.customer.customerId} />
                </div>
              </div>
            )}
          </SectionCard>
        );
      }

      case 'security': {
        if (!userInfo?.persona) return null;
        const byProject = userInfo?.permissions?.byProject || {};
        const projectIds = Object.keys(byProject);
        const isDevAdmin = userInfo?.permissions?.isDeveloperAdmin;
        return (
          <SectionCard
            title="Security & Privacy"
            subtitle="Your persona and per-project permissions"
            action={
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                <Shield size={12} />
                {(userInfo?.persona || '').replace(/_/g, ' ')}
              </span>
            }
          >
            {isDevAdmin && (
              <div className="rounded-xl border border-flamingo-200 bg-gradient-to-r from-flamingo-50 to-flamingo-50/30 p-4 flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-flamingo-500 text-white flex items-center justify-center flex-shrink-0">
                  <BadgeCheck size={16} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-flamingo-900">Developer Admin</p>
                  <p className="text-xs text-flamingo-700 mt-0.5">
                    Full access across all projects under your organisation. RBAC permission
                    checks are bypassed for this persona.
                  </p>
                </div>
              </div>
            )}

            {!isDevAdmin && (
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
                  Project Permissions
                </p>
                {projectIds.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center">
                    <div className="w-12 h-12 mx-auto rounded-xl bg-gray-100 flex items-center justify-center">
                      <AlertCircle size={20} className="text-gray-400" />
                    </div>
                    <p className="mt-3 text-sm font-medium text-gray-700">No project assignments</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Ask your administrator to assign you a role on one or more projects.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {projectIds.map((pid) => {
                      const perms = byProject[pid] || [];
                      return (
                        <div
                          key={pid}
                          className="rounded-xl border border-gray-200 bg-white p-4 hover:border-flamingo-300 hover:shadow-sm transition-all"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-900">
                              <Briefcase size={14} className="text-gray-500" />
                              Project #{pid}
                            </span>
                            <span className="text-xs text-gray-500">
                              {perms.length} permission{perms.length === 1 ? '' : 's'}
                            </span>
                          </div>
                          {perms.length === 0 ? (
                            <p className="text-xs text-gray-400 italic">No permissions on this project</p>
                          ) : (
                            <div className="flex flex-wrap gap-1.5">
                              {perms.map((perm) => (
                                <span
                                  key={perm}
                                  className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-flamingo-50 text-flamingo-700 border border-flamingo-100"
                                >
                                  {perm}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </SectionCard>
        );
      }

      case 'password': {
        // Lightweight password-strength rubric — matches the helper copy below.
        const rules = [
          { test: (pw) => pw.length >= 8, label: 'At least 8 characters' },
          { test: (pw) => /[A-Z]/.test(pw), label: 'One uppercase letter' },
          { test: (pw) => /[a-z]/.test(pw), label: 'One lowercase letter' },
          { test: (pw) => /[0-9]/.test(pw), label: 'One number' },
          { test: (pw) => /[^A-Za-z0-9]/.test(pw), label: 'One special character' },
        ];
        const passed = rules.filter((r) => r.test(newPassword)).length;
        const strength = newPassword.length === 0 ? 0 : passed;
        const strengthMeta =
          strength <= 2
            ? { label: 'Weak', color: 'bg-rose-500', text: 'text-rose-600' }
            : strength <= 3
            ? { label: 'Fair', color: 'bg-slate-400', text: 'text-slate-600' }
            : strength <= 4
            ? { label: 'Good', color: 'bg-flamingo-400', text: 'text-flamingo-600' }
            : { label: 'Strong', color: 'bg-flamingo-600', text: 'text-flamingo-700' };
        const confirmMatches = confirmPassword.length > 0 && confirmPassword === newPassword;
        const confirmMismatched = confirmPassword.length > 0 && confirmPassword !== newPassword;
        return (
          <SectionCard
            title="Change Password"
            subtitle="Use a unique password you don't reuse on other sites"
            action={
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
                <KeyRound size={12} />
                Security
              </span>
            }
          >
            <form onSubmit={handlePasswordChange} className="space-y-5 max-w-xl">
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                  Current Password
                </label>
                <CustomInput
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                  New Password
                </label>
                <CustomInput
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />

                {newPassword.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${strengthMeta.color} transition-all duration-300`}
                          style={{ width: `${(strength / rules.length) * 100}%` }}
                        />
                      </div>
                      <span className={`text-xs font-semibold ${strengthMeta.text}`}>
                        {strengthMeta.label}
                      </span>
                    </div>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      {rules.map((r) => {
                        const ok = r.test(newPassword);
                        return (
                          <li
                            key={r.label}
                            className={`flex items-center gap-1.5 text-xs ${
                              ok ? 'text-flamingo-600' : 'text-gray-500'
                            }`}
                          >
                            <CheckCircle
                              size={12}
                              className={ok ? 'text-flamingo-500' : 'text-gray-300'}
                            />
                            {r.label}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                  Confirm New Password
                </label>
                <CustomInput
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                {confirmMismatched && (
                  <p className="mt-1.5 text-xs text-rose-600 flex items-center gap-1">
                    <AlertCircle size={12} />
                    Passwords don't match
                  </p>
                )}
                {confirmMatches && (
                  <p className="mt-1.5 text-xs text-flamingo-600 flex items-center gap-1">
                    <CheckCircle size={12} />
                    Passwords match
                  </p>
                )}
              </div>

              <div className="pt-2">
                <PrimaryButton
                  type="submit"
                  disabled={isSubmitting || !confirmMatches || strength < 4}
                  className="w-full sm:w-auto px-8"
                >
                  {isSubmitting ? 'Changing Password...' : 'Change Password'}
                </PrimaryButton>
              </div>
            </form>
          </SectionCard>
        );
      }
      case 'roles':
        return userInfo?.persona !== 'CHANNEL_PARTNER' && <RolesDashboard />;
      case 'rbac':
        return userInfo?.persona !== 'CHANNEL_PARTNER' && <ManageRolesPage />;
      default:
        return null;
    }
  };

  return (
    <div className="bg-gradient-to-b from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-6">
        {/* Page title */}
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">Settings</h1>

        <div className="mt-5 flex flex-col lg:flex-row gap-4 sm:gap-6 items-start">
          {/* Vertical tabs — sticky on lg+, sits inline above content on mobile */}
          <aside className="w-full lg:w-64 xl:w-72 flex-shrink-0 lg:sticky lg:top-20 self-start">
            <nav
              className="bg-white rounded-2xl shadow-sm border border-gray-200 p-2 sm:p-3 space-y-1"
              aria-label="Settings sections"
            >
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleSectionChange(item.id)}
                    aria-current={isActive ? "page" : undefined}
                    className={`group flex items-center w-full px-3 py-2.5 text-left rounded-xl transition-all duration-200 ${
                      isActive
                        ? "bg-gradient-to-r from-flamingo-50 to-flamingo-100 text-flamingo-700 shadow-sm"
                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                    }`}
                  >
                    <span
                      className={`flex items-center justify-center w-9 h-9 rounded-lg mr-3 transition-colors shrink-0 ${
                        isActive
                          ? "bg-flamingo-500 text-white"
                          : "bg-gray-100 text-gray-500 group-hover:bg-gray-200 group-hover:text-gray-700"
                      }`}
                    >
                      <Icon size={16} />
                    </span>
                    <span
                      className={`text-sm truncate ${isActive ? "font-semibold" : "font-medium"}`}
                    >
                      {item.label}
                    </span>
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* Main content */}
          <main className="flex-1 min-w-0 w-full">
            <div className="space-y-4 sm:space-y-6">{renderContent()}</div>
          </main>
        </div>
      </div>
    </div>
  );
}

export default Profile;